import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import cl from 'classnames';
import { throttle } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useCreateStore, useProvider } from 'mobx-store-provider';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { useClickAway, useEffectOnce } from 'react-use';
import { Transforms } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import styled from 'styled-components';

import { useDolEditorStore } from '../../../../dol-editor.store';
import { TableBlogClassNames } from '../../const';
import {
  getInitialStateTableAdminView,
  TableAdminViewStore,
  useAdminTableStore,
} from './table-adminview.store';
import { TableBaseProps } from './types';
import { withRootElement } from '../../../../../../slatePlugins/common/hocs/current-element.store';
import { verticalSpace } from '@doltech/utils/lib/css-style';

const AdminTableExtra = dynamic<any>(
  () => import('./AdminTableExtra').then((mod) => mod.AdminTableExtra),
  { ssr: false }
);

const LeftControl = dynamic(() => import('./LeftControl').then((mod) => mod.LeftControl), {
  ssr: false,
});

const Container = styled.div`
  ${verticalSpace(16)}
`;

const TableWrapper = styled.div`
  padding-right: 10px;
  margin-right: -10px;
  padding-top: 10px;
  margin-top: -10px;
  padding-bottom: 15px;
  margin-bottom: -15px;
  overflow: auto;
  overscroll-behavior-x: none;
  position: relative;

  &.inside-blog {
    padding: 0;
    margin-right: 0;

    table {
      table-layout: auto;
      width: 100% !important;
    }
  }

  max-width: 100%;

  ${fromScreen(768)} {
    max-width: 100%;
  }
`;

const AdminTableContainer = styled.div`
  position: relative;
  margin: 0 auto 16px;

  .left-shadow {
    position: absolute;
    left: 0;
    top: 24px;
    bottom: 14px;
    width: 8px;
    pointer-events: none;
    background: linear-gradient(to left, rgba(99, 114, 130, 0) 0, rgba(9, 30, 66, 0.13) 100%);
    display: none;

    &.active {
      display: block;
    }
  }

  .right-shadow {
    position: absolute;
    width: 8px;
    right: 0;
    top: 24px;
    bottom: 14px;
    pointer-events: none;
    background: linear-gradient(to right, rgba(99, 114, 130, 0) 0, rgba(9, 30, 66, 0.13) 100%);
    display: none;

    &.at-edge {
      right: -6px;
    }

    &.active {
      display: block;
    }
  }
`;

const Main = styled.table`
  table-layout: fixed;
  border-collapse: collapse;
  margin: 24px 0 0;

  width: 100%;
  border: 1px solid rgb(193, 199, 208);
  border-left-style: none;
  border-bottom-style: none;

  &.border {
    border-left-style: solid;
  }

  &.inside-blog {
    table-layout: auto;
    width: 100% !important;
  }

  table > tbody > span {
    display: none;
  }

  tbody {
    display: contents !important;
  }
`;

interface AdminTableProps extends TableBaseProps {}

const FormCompletionAdminTableBase = observer((props: AdminTableProps) => {
  const ref = React.useRef();
  const { attributes, element, children, isReadOnly, isTableCompletion } = props;
  const tableStore = useAdminTableStore();
  const dolEditorStore = useDolEditorStore();
  const editor = useSlateStatic();
  const tableRef = React.useRef();
  const [scrollLeft, setScrollLeft] = React.useState(0);
  const [scrollWidth, setScrollWidth] = React.useState(0);
  const [tableViewWidth, setTableViewWidth] = React.useState(0);

  const childrenHeader = children?.[0];
  const childrenBody = children?.slice(1);
  const scrollWidthDiff = scrollWidth - tableViewWidth;
  const isRightShadowActive = scrollWidth > tableViewWidth && scrollLeft < scrollWidthDiff - 10;

  React.useEffect(() => {
    const stopResizeFunc = () => {
      tableStore.stopResize({ editor });
      tableStore.cellSelection.onSelectStop();
    };

    const keyDownFunc = (e) => {
      if (e.key === 'Shift') {
        tableStore.cellSelection.shiftPressStart();
      }
    };

    const keyUpFunc = () => {
      tableStore.cellSelection.shiftPressStop();
    };

    document.addEventListener('mouseup', stopResizeFunc);
    document.addEventListener('keydown', keyDownFunc);
    document.addEventListener('keyup', keyUpFunc);

    const resizeFunc = (e) => {
      tableStore.resizeFrame(e, editor);
    };
    document.addEventListener('mousemove', resizeFunc);

    return () => {
      document.removeEventListener('mouseup', stopResizeFunc);
      document.removeEventListener('mousemove', resizeFunc);
      document.removeEventListener('keydown', keyDownFunc);
      document.removeEventListener('keyup', keyUpFunc);
    };
  }, [tableStore, editor]);

  const shadowCheck = React.useMemo(() => {
    return throttle(() => {
      if (tableRef.current) {
        const elm: HTMLElement = tableRef.current;
        setScrollWidth(elm.scrollWidth);
        setTableViewWidth(elm.getBoundingClientRect().width);
        setScrollLeft(elm.scrollLeft);
      }
    }, 2);
  }, []);

  const stopResize = React.useCallback(() => {
    tableStore.stopResize({ editor });
  }, [tableStore, editor]);

  useEffectOnce(() => {
    tableStore.updateRowHeight();
  });

  React.useEffect(() => {
    if (editor) {
      tableStore.syncInfo({ editor, manual: true });
      tableStore.syncRowColRef({ editor });
    }
  }, [editor, tableStore]);

  React.useEffect(() => {
    tableStore.syncInfo({ editor, manual: false });
    shadowCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dolEditorStore.afterSelectionId]);

  useClickAway(ref, () => {
    tableStore.cellSelection.clearSelection();
    tableStore.cellSelection.onSelectStop();
  });
  return (
    <Container>
      <AdminTableContainer ref={ref} className="admin-table-container">
        {!isReadOnly && tableStore?.isInTable && <LeftControl />}
        <TableWrapper
          ref={tableRef}
          aria-hidden
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              const path = ReactEditor.findPath(editor as any, element);
              Transforms.select(editor, path);
              Transforms.collapse(editor);
            }
          }}
          {...attributes}
          className={cl('table-wrapper', {
            'read-only': isReadOnly,
            'inside-blog': !isTableCompletion && isReadOnly,
          })}
          onScroll={shadowCheck}
        >
          <Main
            data-element-id={element.id}
            id={element.id as string}
            style={{ width: tableStore.tableWidth || 'auto' }}
            className={cl('table-blog-table', `table-${element.id}`, TableBlogClassNames.TABLE, {
              border: element.border,
            })}
            onMouseMove={(e) => tableStore.resizeFrame(e, editor)}
            onMouseUp={stopResize}
          >
            <colgroup contentEditable={false}>
              {(tableStore.cellsWidth as Array<number>).map((width: number, index) => (
                <col style={{ width }} key={index} />
              ))}
            </colgroup>
            <thead>{childrenHeader}</thead>
            <tbody>{childrenBody}</tbody>
            {!isReadOnly && <div className="table-resize-decoration" contentEditable="false" />}
          </Main>
        </TableWrapper>
        <div contentEditable={false} className={cl('left-shadow', { active: scrollLeft > 0 })} />
        <div
          contentEditable={false}
          className={cl('right-shadow', {
            active: isRightShadowActive,
            'at-edge': scrollLeft > scrollWidth - 10,
          })}
        />
        <AdminTableExtra isReadOnly={isReadOnly} isTableCompletion={isTableCompletion} />
      </AdminTableContainer>
    </Container>
  );
});

export const FormCompletionAdminTable = (props: AdminTableProps) => {
  const { element } = props;
  const tableAdminViewCreateStore = useCreateStore(
    TableAdminViewStore,
    getInitialStateTableAdminView(element)
  );
  const TableAdminViewStoreProvider = useProvider(TableAdminViewStore);

  return (
    <TableAdminViewStoreProvider value={tableAdminViewCreateStore}>
      <FormCompletionAdminTableBase {...props} />
    </TableAdminViewStoreProvider>
  );
};

export const FormCompletionAdminTableReadOnly = withRootElement((props: AdminTableProps) => {
  const { attributes, element, children, isTableCompletion } = props;
  const childrenHeader = children?.[0];
  const childrenBody = children?.slice(1);
  const tableWidth = element?.cellsWidth.reduce((acc, val) => acc + val, 0);

  const cellWidths = React.useMemo(
    () =>
      (element?.cellsWidth || []).map((cellWidth) => {
        return `${(cellWidth / tableWidth) * 100}%`;
      }),
    [element?.cellsWidth, tableWidth]
  );

  return (
    <Main
      aria-hidden
      {...attributes}
      data-element-id={element.id}
      id={element.id as string}
      className={cl('admin-table-container', 'read-only', {
        border: element.border,
        'inside-blog': !isTableCompletion,
      })}
      style={{ minWidth: tableWidth }}
    >
      <colgroup contentEditable={false}>
        {(cellWidths as Array<number>).map((width: number, index) => (
          <col style={{ width }} key={index} />
        ))}
      </colgroup>
      <thead>{childrenHeader}</thead>
      <tbody>{childrenBody}</tbody>
    </Main>
  );
});
/*

 */
